import { Box, Tab, Tabs } from "@mui/material";
import { useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { OwnerProvider } from "../../../context/OwnerContext";
import { useRole } from "../../../context/RoleContext";

interface IOwnerTabRoute {
  [key: number]: string;
}

const OwnerTabRoutes: IOwnerTabRoute = {
  0: "info",
  1: "statistics",
  2: "washing-history",
  3: "washing-centers",
  4: "employees",
  5: "relationship-manager",
  6: "support-cases",
  7: "payment-history",
  8: "logs",
  // Phase 38 (D-08) — appended, never renumbered. This map and the <Tab> list in
  // SuperAdminOwnerView are index-coupled with nothing enforcing they agree, so
  // both must always be edited together.
  9: "contract",
};

// RM 5-tab → child-route map. Index 0 ("Profile") maps to "info", matching the
// RMOwnerTableRow landing route (`${_id}/info`). Child routes registered in Plan 05.
const RMOwnerTabRoutes: IOwnerTabRoute = {
  0: "info",
  1: "washing-centers",
  2: "employees",
  3: "statistics",
  4: "contract",
};

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

// ---------------------------------------------------------------------------
// Super-admin view — 10-tab layout (Contract appended at index 9 in Phase 38).
// ---------------------------------------------------------------------------
function SuperAdminOwnerView() {
  const [value, setValue] = useState(0);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    navigate(OwnerTabRoutes[newValue]);
  };

  return (
    <Box className="flex flex-col">
      <Tabs
        value={value}
        onChange={handleTabChange}
        aria-label={t("ownerScreens.innerNavAria")}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label={t("owners.detail.tabInfo")} {...a11yProps(0)} />
        <Tab label={t("owners.detail.tabStatistics")} {...a11yProps(1)} />
        <Tab label={t("owners.detail.tabWashingHistory")} {...a11yProps(2)} />
        <Tab label={t("owners.detail.tabWashingCenters")} {...a11yProps(3)} />
        <Tab label={t("owners.detail.tabEmployees")} {...a11yProps(4)} />
        <Tab label={t("owners.detail.tabRelationshipManager")} {...a11yProps(5)} />
        <Tab label={t("owners.detail.tabSupportCases")} {...a11yProps(6)} />
        <Tab label={t("owners.detail.tabPaymentHistory")} {...a11yProps(7)} />
        <Tab label={t("owners.detail.tabLogs")} {...a11yProps(8)} />
        <Tab label={t("owners.detail.tabContract")} {...a11yProps(9)} />
      </Tabs>

      <Outlet />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// RM-scoped 5-tab view — Phase 27.
// ---------------------------------------------------------------------------
function RMOwnerView() {
  const [value, setValue] = useState(0);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    navigate(RMOwnerTabRoutes[newValue]);
  };

  return (
    <Box className="flex flex-col">
      <Tabs
        value={value}
        onChange={handleTabChange}
        aria-label={t("ownerScreens.innerNavAria")}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label={t("owners.detail.tabProfile")} {...a11yProps(0)} />
        <Tab label={t("owners.detail.tabWashingCenters")} {...a11yProps(1)} />
        <Tab label={t("owners.detail.tabEmployees")} {...a11yProps(2)} />
        <Tab label={t("owners.detail.tabStatistics")} {...a11yProps(3)} />
        <Tab label={t("owners.detail.tabContract")} {...a11yProps(4)} />
      </Tabs>

      <Outlet />
    </Box>
  );
}

// Branch component: reads the role and selects the matching tab layout.
// Rendered inside OwnerProvider so both branches share one owner fetch.
function OwnerTabs() {
  const { isRelationshipManager } = useRole();
  return isRelationshipManager ? <RMOwnerView /> : <SuperAdminOwnerView />;
}

const Owner = () => (
  <OwnerProvider>
    <OwnerTabs />
  </OwnerProvider>
);

export default Owner;
